import type { HeroContent } from '@/content/home';
import { ArrowRight, ShieldCheck } from '@/components/ui/Icons';

export type HeroProps = {
  content: HeroContent;
};

export function Hero({ content }: HeroProps) {
  return (
    <section
      aria-labelledby="hero-headline"
      className="bg-canvas px-6 pb-[120px] pt-[96px] md:px-[72px]"
    >
      <div className="mx-auto flex max-w-[1296px] flex-col items-center justify-between gap-16 lg:flex-row lg:gap-24">
        <div className="flex w-full max-w-[640px] flex-col gap-8">
          <span className="inline-flex items-center gap-2 self-start rounded-full bg-cloak-tint px-3 py-1.5">
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-cloak-accent" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-cloak-accent">
              {content.eyebrow}
            </span>
          </span>

          <h1
            id="hero-headline"
            className="text-[40px] font-bold leading-[1.1] tracking-[-0.9px] text-ink md:text-[48px]"
          >
            {content.headline}
          </h1>

          <p className="text-[17px] leading-[1.55] text-graphite md:text-[18px]">
            {content.subhead}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href="https://cloakling.dev"
              className="inline-flex items-center gap-2.5 rounded-lg bg-cloak-accent px-[22px] py-3.5 text-[15px] font-semibold text-page no-underline transition-colors hover:bg-cloak-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloak-accent focus-visible:ring-offset-2"
            >
              {content.primaryCta}
              <ArrowRight size={16} />
            </a>
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-2 rounded-lg border border-rule bg-page px-[22px] py-[13px] text-[15px] font-semibold text-ink no-underline hover:bg-canvas"
            >
              {content.secondaryCta}
            </a>
          </div>

          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-pencil" />
            <p className="text-[13px] font-medium text-pencil">{content.supporting}</p>
          </div>
        </div>

        <CloakPreviewCard />
      </div>
    </section>
  );
}

/**
 * Static visual mock of a cloaked document, mirroring the hero preview frame
 * (Pencil node r5sZYO). Decorative — content is illustrative, not real data.
 */
function CloakPreviewCard() {
  return (
    <div
      aria-hidden="true"
      className="w-full max-w-[560px] overflow-hidden rounded-[14px] border border-black/10 bg-page shadow-[0_18px_48px_rgba(20,20,40,0.08),0_2px_6px_rgba(20,20,40,0.04)]"
    >
      <div className="flex h-9 items-center gap-2 border-b border-black/5 bg-canvas px-3.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
        <span className="ml-3 text-[11px] font-medium text-pencil">
          northwood-pediatrics-bill.pdf
        </span>
      </div>

      <div className="flex items-center justify-between gap-3 px-7 pb-3 pt-5">
        <div className="flex items-center gap-2">
          <span className="rounded bg-cloak-tint px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cloak-accent">
            CLOAKED
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-medium text-pencil">
          <span className="h-1.5 w-1.5 rounded-full bg-cloak-accent" />
          12 entities cloaked
        </div>
      </div>

      <div className="flex flex-col gap-3.5 px-7 pb-7 pt-2">
        <h3 className="text-[18px] font-bold text-ink">Statement of Account</h3>
        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-pencil">
          OUTPATIENT VISIT · BILLING SUMMARY
        </p>

        <CloakLine label="This bill is addressed to" cloak="Aletta Whitlock" />
        <CloakLine label="for patient" cloak="Theo Whitlock" />
        <CloakLine label="covered under" cloak="Northwind Health Plan" />
        <CloakLine label="for the visit on" cloak="Mar 14" suffix="balanced" balance="$ 412" />
        <CloakLine
          label="Send billing questions to"
          cloak="billing@northwood-peds.example"
        />
        <CloakLine label="or call" cloak="(555) 419-2030" suffix="weekdays 9–5." />
      </div>

      <div className="border-t border-black/5" />

      <div className="flex items-center justify-between gap-2 px-7 py-3.5">
        <div className="flex items-center gap-2 text-[11px] text-pencil">
          <span className="h-1.5 w-1.5 rounded-full bg-cloak-accent" />
          Strategy: Synthetic — consistent realistic substitutes
        </div>
        <span className="rounded-md border border-cloak-accent/20 bg-page px-2.5 py-1.5 text-[11px] font-semibold text-cloak-accent">
          Update Cloak
        </span>
      </div>
    </div>
  );
}

function CloakLine({
  label,
  cloak,
  suffix,
  balance,
}: {
  label: string;
  cloak: string;
  suffix?: string;
  balance?: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[12px] leading-[1.5] text-graphite">
      <span>{label}</span>
      <span className="rounded bg-cloak-tint px-1.5 py-0.5 font-semibold text-cloak-accent">
        {cloak}
      </span>
      {suffix && <span>{suffix}</span>}
      {balance && (
        <span className="rounded bg-cloak-tint px-1.5 py-0.5 font-semibold text-cloak-accent">
          {balance}
        </span>
      )}
    </div>
  );
}
